/**
 * OrgUnitsModal — divisiones y áreas de la empresa.
 *
 * El CRUD de `Division`/`Area` existía en la API desde hace mucho **sin una sola línea de
 * interfaz**: se podían crear por consola y nada más, así que en producción hay 0 divisiones
 * y 0 áreas y la vista de unidades del organigrama devolvía una lista plana. Esta es la
 * pantalla que faltaba para que ese eje se pueda usar.
 *
 * ⚠️ La acción principal para dejar de usar una unidad es **desactivar**, no borrar: sale
 * del organigrama y el histórico queda. Borrar solo se ofrece cuando no arrastra nada
 * (el backend lo vuelve a comprobar y responde 409).
 */
import { useState } from 'react'
import { Pencil, Plus, Power, Trash2 } from 'lucide-react'

import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { IconButton } from '@/components/IconButton'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { useToast } from '@/components/Toast'
import { usePermission } from '@/hooks/usePermission'
import { getApiErrorMessage } from '@/lib/apiClient'

import {
  useAreas, useDeleteArea, useDeleteDivision, useDivisions, useSaveArea, useSaveDivision,
  type Area, type Division,
} from '../hooks/useOrgChart'
import { useEmployees } from '../hooks/usePayroll'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

type Borrador = {
  id?: string
  code: string
  name: string
  manager: string
  division?: string
  is_active: boolean
}

const VACIO: Borrador = { code: '', name: '', manager: '', division: '', is_active: true }

export function OrgUnitsModal({ onClose }: { onClose: () => void }) {
  const toast = useToast()
  const { can } = usePermission()
  const puedeEditar = can('payroll.org_units', 'edit')
  const puedeCrear = can('payroll.org_units', 'create')
  const puedeBorrar = can('payroll.org_units', 'delete')

  const { data: divData, isLoading: cargandoDiv } = useDivisions()
  const { data: areaData, isLoading: cargandoAreas } = useAreas()
  const { data: empData } = useEmployees({ active: 'true', page_size: 500 })
  const saveDivision = useSaveDivision()
  const saveArea = useSaveArea()
  const delDivision = useDeleteDivision()
  const delArea = useDeleteArea()
  const { confirm, modalProps } = useConfirmModal()

  const [editandoDiv, setEditandoDiv] = useState<Borrador | null>(null)
  const [editandoArea, setEditandoArea] = useState<Borrador | null>(null)

  const empleados = empData?.employees || []
  const divisiones = divData?.divisions || []
  const areas = areaData?.areas || []

  const guardar = async (tipo: 'division' | 'area', b: Borrador) => {
    if (!b.code.trim() || !b.name.trim()) {
      toast.error('El código y el nombre son obligatorios.')
      return
    }
    const payload: Record<string, unknown> = {
      id: b.id,
      code: b.code.trim().toUpperCase(),
      name: b.name.trim(),
      manager: b.manager || null,
      is_active: b.is_active,
    }
    if (tipo === 'area') payload.division = b.division || null
    try {
      if (tipo === 'division') await saveDivision.mutateAsync(payload as never)
      else await saveArea.mutateAsync(payload as never)
      toast.success(tipo === 'division' ? 'División guardada.' : 'Área guardada.')
      if (tipo === 'division') setEditandoDiv(null)
      else setEditandoArea(null)
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  const alternarActiva = async (tipo: 'division' | 'area', u: Division | Area) => {
    const mut = tipo === 'division' ? saveDivision : saveArea
    try {
      await mut.mutateAsync({ id: u.id, is_active: !u.is_active } as never)
      toast.success(u.is_active ? 'Desactivada.' : 'Reactivada.')
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  const borrar = (tipo: 'division' | 'area', u: Division | Area) => {
    confirm({
      message: `¿Eliminar «${u.name}»? Si prefieres conservar el historial, desactívala en vez de borrarla.`,
      confirmLabel: 'Eliminar',
      variant: 'danger',
      onConfirm: async () => {
        try {
          if (tipo === 'division') await delDivision.mutateAsync(u.id)
          else await delArea.mutateAsync(u.id)
          toast.success('Eliminada.')
        } catch (e) {
          toast.error(getApiErrorMessage(e))
        }
      },
    })
  }

  const formulario = (tipo: 'division' | 'area', b: Borrador,
                      set: (b: Borrador | null) => void) => (
    <div className="rounded-lg border border-divider bg-glass-3 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField
          dense
          label="Código"
          value={b.code}
          onChange={e => set({ ...b, code: e.target.value })}
          placeholder="OPS"
          inputProps={{ maxLength: 20 }}
        />
        <TextField
          dense
          className="sm:col-span-2"
          label="Nombre"
          value={b.name}
          onChange={e => set({ ...b, name: e.target.value })}
          placeholder="Operaciones"
          inputProps={{ maxLength: 120 }}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tipo === 'area' && (
          <SelectField
            dense
            label="División"
            value={b.division || ''}
            onChange={e => set({ ...b, division: e.target.value })}
          >
            <option value="">— Sin división —</option>
            {divisiones.filter(d => d.is_active).map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </SelectField>
        )}
        <SelectField
          dense
          label={tipo === 'division' ? 'Gerente de división' : 'Jefe de área'}
          value={b.manager}
          onChange={e => set({ ...b, manager: e.target.value })}
        >
          <option value="">— Sin asignar —</option>
          {empleados.map((e: { id: string; full_name: string }) => (
          <option key={e.id} value={e.id}>{e.full_name}</option>
          ))}
        </SelectField>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={() => set(null)}>Cancelar</Button>
        <Button size="sm" onClick={() => guardar(tipo, b)}
                isPending={tipo === 'division' ? saveDivision.isPending : saveArea.isPending}>
          {b.id ? 'Guardar' : 'Crear'}
        </Button>
      </div>
    </div>
  )

  const fila = (tipo: 'division' | 'area', u: Division | Area, detalle: string) => (
    <li key={u.id}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg border border-divider ${
          u.is_active ? 'bg-surface' : 'bg-glass-3 opacity-70'}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-content truncate">
          <span className="font-mono text-xs text-content-muted mr-2">{u.code}</span>
          {u.name}
          {!u.is_active && <span className="ml-2 text-xs text-content-subtle">(inactiva)</span>}
        </p>
        <p className="text-xs text-content-subtle truncate">{detalle}</p>
      </div>
      {puedeEditar && (
        <>
          <IconButton aria-label="Editar" icon={<Pencil size={14} />}
                      onClick={() => {
                        const b: Borrador = {
                          id: u.id, code: u.code, name: u.name,
                          manager: u.manager || '', is_active: u.is_active,
                          division: (u as Area).division || '',
                        }
                        if (tipo === 'division') setEditandoDiv(b)
                        else setEditandoArea(b)
                      }} />
          <IconButton aria-label={u.is_active ? 'Desactivar' : 'Reactivar'}
                      icon={<Power size={14} />}
                      onClick={() => alternarActiva(tipo, u)} />
        </>
      )}
      {puedeBorrar && (
        <IconButton aria-label="Eliminar" icon={<Trash2 size={14} />}
                    onClick={() => borrar(tipo, u)} />
      )}
    </li>
  )

  return (
    <Modal open onClose={onClose} title="Divisiones y áreas" size="xl"
           actions={<ModalCancelButton onClick={onClose} label="Cerrar" />}>
      <div className="space-y-6">
        <p className="text-xs text-content-muted">
          La estructura por unidades es opcional: el organigrama por jefaturas funciona sin
          ella. Sirve cuando la empresa tiene gerencias y áreas formales, y se completa
          asignando el área en la ficha de cada trabajador.
        </p>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-content">
              Divisiones {divisiones.length > 0 && (
                <span className="text-content-subtle font-normal">({divisiones.length})</span>
              )}
            </h3>
            {puedeCrear && !editandoDiv && (
              <Button size="sm" variant="secondary" onClick={() => setEditandoDiv({ ...VACIO })}>
                <Plus size={14} /> Nueva división
              </Button>
            )}
          </div>
          {editandoDiv && (
            <div className="mb-3">{formulario('division', editandoDiv, setEditandoDiv)}</div>
          )}
          {cargandoDiv ? (
            <p className="text-sm text-content-muted">Cargando…</p>
          ) : divisiones.length === 0 ? (
            <p className="text-sm text-content-subtle px-3 py-4 bg-surface-muted rounded-lg border border-divider">
              Todavía no hay divisiones. El organigrama funciona igual por jefaturas.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {divisiones.map(d => fila('division', d,
                [d.manager_name ? `Gerente: ${d.manager_name}` : 'Sin gerente',
                 `${d.areas_count} área(s)`].join(' · ')))}
            </ul>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-content">
              Áreas {areas.length > 0 && (
                <span className="text-content-subtle font-normal">({areas.length})</span>
              )}
            </h3>
            {puedeCrear && !editandoArea && (
              <Button size="sm" variant="secondary" onClick={() => setEditandoArea({ ...VACIO })}>
                <Plus size={14} /> Nueva área
              </Button>
            )}
          </div>
          {editandoArea && (
            <div className="mb-3">{formulario('area', editandoArea, setEditandoArea)}</div>
          )}
          {cargandoAreas ? (
            <p className="text-sm text-content-muted">Cargando…</p>
          ) : areas.length === 0 ? (
            <p className="text-sm text-content-subtle px-3 py-4 bg-surface-muted rounded-lg border border-divider">
              Todavía no hay áreas.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {areas.map(a => fila('area', a,
                [a.division_name || 'Sin división',
                 a.manager_name ? `Jefe: ${a.manager_name}` : 'Sin jefe',
                 `${a.employees_count} persona(s)`].join(' · ')))}
            </ul>
          )}
        </section>
      </div>
      <ConfirmModal {...modalProps} />
    </Modal>
  )
}
